import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { logger } from './utils/logger';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate that all required environment variables are present
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
];

const missingEnvVars = requiredEnvVars.filter(
  envVar => !import.meta.env[envVar]
);
if (missingEnvVars.length > 0) {
  throw new Error(
    `Missing required environment variables: ${missingEnvVars.join(', ')}`
  );
}

// Firebase configuration validated and ready
logger.debug('Firebase configuration validated', undefined, {
  component: 'Firebase',
  operation: 'Initialize',
  extra: {
    environment: import.meta.env.VITE_ENV,
    nodeEnvironment: import.meta.env.MODE,
    configurationValid: true,
  },
});

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Point at the local emulator suite. Opt-in via env so this can never engage
// in a deployed build: only .env.e2e (and a developer running the emulators
// deliberately) sets the flag.
export const usingEmulators =
  import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

if (usingEmulators) {
  const host = import.meta.env.VITE_EMULATOR_HOST || '127.0.0.1';
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8082);
  connectStorageEmulator(storage, host, 9199);
  logger.info('Firebase emulators connected', {
    component: 'Firebase',
    operation: 'Initialize',
    extra: { host },
  });
}

// Initialize App Check (monitor mode — does not block requests yet)
// Must be read through import.meta.env: `process` does not exist in the
// browser, so the CRA-era name both crashed `vite dev` and silently resolved
// to undefined in production builds, leaving App Check permanently off.
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
if (recaptchaSiteKey) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    logger.info('App Check initialized', {
      component: 'Firebase',
      operation: 'AppCheck',
    });
  } catch (error) {
    logger.warn('App Check initialization failed', undefined, {
      component: 'Firebase',
      operation: 'AppCheck',
    });
  }
} else {
  logger.debug(
    'App Check not configured (VITE_RECAPTCHA_SITE_KEY not set)',
    undefined,
    {
      component: 'Firebase',
      operation: 'AppCheck',
    }
  );
}

// Log Firebase initialization status
logger.info('Firebase services initialized', {
  component: 'Firebase',
  operation: 'Initialize',
  extra: {
    mode: 'production',
    servicesReady: true,
  },
});

// Utility function to verify user authentication
export const getCurrentUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.uid;
};
